import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, ChevronRight, Loader2, Plus } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/site/Badges";
import { btnPrimary, btnSecondary, FieldError, inputCls } from "@/components/admin/AdminLayout";
import { StatusToggle } from "@/pages/admin/ProjectsPage";

const NewProjectForm = ({ onCancel }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("In Progress");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/projects", { title, status, description: "" });
      navigate(`/admin/projects/${data.id}?upload=1`);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} noValidate className="space-y-3" data-testid="picker-new-project-form">
      <label htmlFor="picker-title" className="text-sm text-zinc-300">Title / Make &amp; Model <span className="text-zinc-500">(optional)</span></label>
      <Input id="picker-title" autoFocus placeholder="e.g. 1969 Camaro — leave blank to just show photos" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="picker-new-project-title-input" />
      <FieldError msg={error} testId="picker-new-project-title-error" />
      <StatusToggle value={status} onChange={setStatus} prefix="picker-new-project" />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} className={`${btnPrimary} flex-1`} data-testid="picker-new-project-save-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create & add photos
        </Button>
        <Button type="button" onClick={onCancel} className={btnSecondary} data-testid="picker-new-project-cancel-button">Back</Button>
      </div>
    </form>
  );
};

export const ProjectPicker = ({ open, onOpenChange }) => {
  const [projects, setProjects] = useState(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCreating(false);
    api.get("/admin/projects").then((r) => setProjects([...r.data].sort((a, b) => b.updated_at.localeCompare(a.updated_at))));
  }, [open]);
  const change = (v) => onOpenChange(v);
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto rounded-lg border-zinc-700 bg-[#1A1A1E] text-[#FAF7F2]" data-testid="project-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Add photos & videos</DialogTitle>
          <DialogDescription className="text-zinc-400">{creating ? "Add a title if you like, or leave it blank." : "Pick the project they belong to."}</DialogDescription>
        </DialogHeader>
        {creating ? (
          <NewProjectForm onCancel={() => setCreating(false)} />
        ) : (
          <div className="space-y-2">
            <button onClick={() => setCreating(true)} className="flex min-h-[64px] w-full items-center gap-3 rounded-lg border border-dashed border-[#FFD23F]/60 px-4 text-left text-[#FFD23F] hover:bg-[#FFD23F]/10" data-testid="picker-new-project-button">
              <Plus className="h-6 w-6" /> <span className="font-sub text-lg font-semibold uppercase">New project</span>
            </button>
            {!projects && <p className="py-4 text-center text-zinc-400">Loading…</p>}
            {projects?.map((p) => (
              <Link key={p.id} to={`/admin/projects/${p.id}?upload=1`} className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-[#121214] p-2.5 hover:border-[#FFD23F]/60" data-testid={`picker-project-${p.id}`}>
                <div className="h-12 w-16 shrink-0 overflow-hidden rounded bg-black">
                  {p.cover?.url ? <img src={mediaUrl(p.cover.url)} alt="" className="h-full w-full object-cover" /> : <Camera className="m-auto mt-3 h-5 w-5 text-zinc-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-zinc-100">{p.title || "Untitled project"}</p>
                  <StatusBadge status={p.status} />
                </div>
                <ChevronRight className="h-5 w-5 text-zinc-500" />
              </Link>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
